import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [syncing, setSyncing] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const sessionId = searchParams.get('session_id');

  // Sync subscription on mount
  useEffect(() => {
    const syncSubscription = async () => {
      if (!sessionId) {
        setSyncError("Session ID não encontrado");
        setSyncing(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('sync-subscription', {
          body: { session_id: sessionId }
        });

        if (error) throw error;

        if (data.success) {
          setPlanName(data.plan_name);
          toast({
            title: "Plano atualizado!",
            description: `Seu plano ${data.plan_name} está ativo.`,
          });
        } else {
          throw new Error(data.error || 'Erro ao sincronizar plano');
        }
      } catch (error: any) {
        console.error('Sync error:', error);
        setSyncError(error.message || 'Erro ao sincronizar. Tente novamente.');
      } finally {
        setSyncing(false);
      }
    };

    syncSubscription();
  }, [sessionId]);

  // Countdown timer
  useEffect(() => {
    if (syncing) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/account');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, syncing]);

  const handleRetry = async () => {
    setSyncing(true);
    setSyncError(null);
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {syncing ? (
            <>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
              <CardTitle className="text-2xl">Processando seu pagamento...</CardTitle>
              <CardDescription>
                Aguarde enquanto confirmamos sua assinatura
              </CardDescription>
            </>
          ) : syncError ? (
            <>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-10 w-10 text-destructive" />
              </div>
              <CardTitle className="text-2xl">Ops! Algo deu errado</CardTitle>
              <CardDescription>
                Não conseguimos confirmar seu pagamento automaticamente
              </CardDescription>
            </>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl">Pagamento confirmado!</CardTitle>
              <CardDescription>
                {planName ? `Seu plano ${planName} está ativo` : 'Seu plano foi atualizado com sucesso'}
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {syncError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {syncError}
              </AlertDescription>
            </Alert>
          )}

          {sessionId && !syncing && (
            <div className="rounded-lg bg-muted p-4">
              <p className="text-sm text-muted-foreground mb-1">ID da transação</p>
              <p className="text-xs font-mono break-all">{sessionId}</p>
            </div>
          )}
          
          <div className="text-center space-y-4">
            {!syncing && !syncError && (
              <p className="text-sm text-muted-foreground">
                Redirecionando em {countdown} segundos...
              </p>
            )}
            
            {syncError ? (
              <div className="space-y-2">
                <Button onClick={handleRetry} className="w-full">
                  Tentar Novamente
                </Button>
                <Button onClick={() => navigate('/account')} variant="outline" className="w-full">
                  Ir para Minha Conta
                </Button>
              </div>
            ) : (
              <Button onClick={() => navigate('/account')} className="w-full" disabled={syncing}>
                {syncing ? 'Aguarde...' : 'Ir para Minha Conta'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
